import {
  API_BASE,
  isProductionApiUrl,
  isRenderPreviewHostname,
  previewBackendOrigin,
} from "@/utils/apiBase";

/**
 * Rewrite browser fetches for `/api...` so a separately-hosted API can sit on
 * a different origin without forcing a repo-wide component rewrite.
 *
 * When `REACT_APP_API_BASE_URL` is unset this is effectively a no-op, except
 * on a Render preview host: those pages must not follow the copied `/api`
 * rewrite to the production backend. If the preview backend cannot be
 * derived, the call is rejected instead of sent same-origin.
 */
export function installApiBaseFetchShim(): void {
  if (typeof window === "undefined" || typeof fetch !== "function") return;

  const preview = isRenderPreviewHostname(window.location.hostname);
  const previewBase = preview ? (API_BASE || previewBackendOrigin(window.location.hostname)) : "";
  if (!preview && (!API_BASE || API_BASE === window.location.origin)) return;

  const globalWindow = window as typeof window & {
    __tsApiBaseFetchShimInstalled?: boolean;
  };
  if (globalWindow.__tsApiBaseFetchShimInstalled) return;
  globalWindow.__tsApiBaseFetchShimInstalled = true;

  const originalFetch = window.fetch.bind(window);
  const sameOriginPrefix = `${window.location.origin}/api`;
  const apiBase = preview ? previewBase : API_BASE;
  const apiBasePrefix = apiBase ? `${apiBase}/api` : "";

  const rewriteUrl = (raw: string): string => {
    const absolute = raw.startsWith("/api")
      ? `${window.location.origin}${raw}`
      : raw;
    let parsed: URL;
    try {
      parsed = new URL(absolute, window.location.origin);
    } catch {
      return raw;
    }
    const sameOriginApi = parsed.origin === window.location.origin && parsed.pathname.startsWith("/api");
    const productionApi = isProductionApiUrl(parsed.href) && parsed.pathname.startsWith("/api");
    if (preview && (sameOriginApi || productionApi)) {
      if (!apiBasePrefix || isProductionApiUrl(apiBase)) {
        throw new TypeError("Preview blocked a call to the production API");
      }
      return `${apiBasePrefix}${parsed.pathname.slice("/api".length)}${parsed.search}`;
    }
    if (raw.startsWith("/api") && apiBasePrefix) return `${apiBase}${raw}`;
    if (raw.startsWith(sameOriginPrefix) && apiBasePrefix) {
      return `${apiBasePrefix}${raw.slice(sameOriginPrefix.length)}`;
    }
    return raw;
  };

  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    try {
      if (typeof input === "string") {
        return originalFetch(rewriteUrl(input), init);
      }
      if (input instanceof URL) {
        return originalFetch(new URL(rewriteUrl(input.toString())), init);
      }
      if (typeof Request !== "undefined" && input instanceof Request) {
        const rewrittenUrl = rewriteUrl(input.url);
        if (rewrittenUrl === input.url) return originalFetch(input, init);
        const rewrittenRequest = new Request(rewrittenUrl, input);
        return originalFetch(rewrittenRequest, init);
      }
      return originalFetch(input, init);
    } catch (err) {
      return Promise.reject(err);
    }
  }) as typeof window.fetch;
}
